<script lang="ts" setup>
import { useStoreConfig } from '~/store/storeConfig'
import Button from '~/components/Button.vue'
import Lightbox from 'vue-easy-lightbox'
import getAdditionalServiceNameById from '~/utils/shipzeePro/getAdditionalServiceNameById'
import lockScroll from '~/utils/lockScroll'
import type { PackageInterface } from '~/types/shipzeePro/packagesTypes'

const { isMobile } = useStoreConfig()
const { t } = useLang()

const props = defineProps<{
  visible: boolean
  item: PackageInterface | null
}>()

const visible = ref<Boolean>(props.visible)
const lastPosition = ref(0)
const showLightbox = ref(false)

const galleryImages = ref<string[]>([])
const activeImage = ref(0)

watch(
  () => props.visible,
  (isVisible) => {
    visible.value = isVisible
    if (isVisible) {
      lastPosition.value = window?.scrollY ?? 0
    }
    lockScroll(isVisible, lastPosition.value)
  }
)
const emit = defineEmits(['handle'])

function handle(value: boolean) {
  emit('handle', value)
}

function openLightBox(images: string[], imageNumber: number = 0) {
  galleryImages.value = images
  activeImage.value = imageNumber
  showLightbox.value = true
}
</script>

<template>
  <lazy-client-only>
    <Modal
      v-if="item"
      :visible="visible"
      class="modal-component modal-md"
      :class="{ 'modal-mobile': isMobile }"
      @handle="handle">
      <template #header>
        <h5 class="text-text1 dark:text-dmText1">
          {{ t('site_pro.additional_services.inspection_packages.title') }}
        </h5>
      </template>
      <div class="content">
        <div v-for="(service, serviceIndex) in item?.additional_services" :key="serviceIndex" class="service-block">
          <h6 class="h6">{{ getAdditionalServiceNameById(service.service_id) }}</h6>

          <div class="header-divider" />

          <div class="font-bold text-text1 dark:text-dmText1 pb-2">
            {{ `${t('site_pro.additional_services.inspection_packages.instructions')}:` }}
          </div>
          <div class="font-normal text-text2 dark:text-dmText2 pb-5">{{ service.arguments.instructions }}</div>
          <div class="font-bold text-text1 dark:text-dmText1 pb-2">
            {{ `${t('site_pro.additional_services.inspection_packages.findings')}:` }}
          </div>

          <div v-if="service.arguments.findings" class="font-normal text-text2 dark:text-dmText2 pb-5">
            {{ service.arguments.findings }}
          </div>
          <template v-if="service.arguments.photos && service.arguments.photos.length > 0">
            <div class="photo-wrapper">
              <img
                v-for="(photo, photoIndex) in service.arguments.photos"
                :key="`item-photo-${photoIndex}`"
                :src="photo"
                @click="openLightBox(service.arguments.photos, photoIndex)" />
            </div>
          </template>
          <div class="text-xs text-neutral3 dark:text-dmText3">{{ service.completed_at }}</div>
        </div>
        <Button class="w-full" button-type="secondary" type="submit" @click="handle(false)">
          {{ t('common.close') }}
        </Button>

        <Lightbox :visible="showLightbox" :imgs="galleryImages" :index="activeImage" @hide="showLightbox = false" />
      </div>
    </Modal>
  </lazy-client-only>
</template>

<style lang="postcss" scoped>
.service-block {
  @apply bg-neutral7 border border-neutral6 dark:border-dmGray1 mb-5 p-2 rounded;
}
.photo-wrapper {
  @apply flex flex-wrap gap-2 pb-2;

  img {
    @apply h-20 w-20 rounded-[0.5rem];
  }
}
.header-divider {
  @apply h-[1px] w-full bg-neutral6 my-5;
}
</style>
